import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

interface FuenteDatos {
  softnet: boolean;
  eva: boolean;
}

interface DatosFormProps {
  onSaved: () => void;
}

export default function DatosForm({ onSaved }: DatosFormProps) {
  const [fuentes, setFuentes] = useState<FuenteDatos>({
    softnet: true,
    eva: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (source: keyof FuenteDatos) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;

    // Si intenta desmarcar la última fuente activa, no permitir
    if (!newValue && !fuentes[source === 'softnet' ? 'eva' : 'softnet']) {
      setError('Debe mantener al menos una fuente de datos activa');
      return;
    }

    setError(null);
    setFuentes(prev => ({
      ...prev,
      [source]: newValue,
    }));
  };

  const handleSave = async () => {
    // Validar que al menos una fuente esté activa
    if (!fuentes.softnet && !fuentes.eva) {
      setError('Debe seleccionar al menos una fuente de datos');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Guardar en localStorage por ahora
      localStorage.setItem('comisiones_fuentes_datos', JSON.stringify(fuentes));

      // Llamar callback
      onSaved();

      setTimeout(() => {
        setSaving(false);
      }, 500);
    } catch (err) {
      setError('Error al guardar la configuración');
      setSaving(false);
    }
  };

  // Cargar configuración al montar
  useEffect(() => {
    const saved = localStorage.getItem('comisiones_fuentes_datos');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setFuentes(parsed);
      } catch {
        // Usar valores por defecto
      }
    }
  }, []);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Configuración de Fuentes de Datos
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Selecciona las fuentes de datos que se utilizarán para calcular las comisiones.
        Al menos una fuente debe estar activa.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={fuentes.softnet}
                onChange={handleChange('softnet')}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Softnet
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Obtiene datos desde sistema externo
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={fuentes.eva}
                onChange={handleChange('eva')}
                color="primary"
                disabled={true}
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.disabled' }}>
                  EVA (Base de Datos Local)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Obtiene datos desde la base de datos de EVA (❗En desarrollo ❗)
                </Typography>
              </Box>
            }
          />
        </FormGroup>
      </Paper>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Fuente activa:</strong> {fuentes.softnet ? 'Softnet' : 'EVA'}
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          Los cálculos de comisiones utilizarán únicamente los datos de la fuente seleccionada.
        </Typography>
      </Alert>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Guardando...' : 'Guardar Configuración'}
        </Button>
      </Box>
    </Box>
  );
}
